// Hooks
import { useState, useCallback } from 'react';
import useAccessToken from './useAccessToken';

// Interfaces
import { RequestConfig } from '../interfaces/RequestConfig';

const useHttp = <T,>(): {
    isLoading: boolean,
    error: string | null,
    sendRequest: (
        requestConfig: RequestConfig,
        parseData?: (data: T) => void,
        errorCallback?: (error: string) => void
    ) => void,
} => {
    const { getAccessToken, latestAccessToken } = useAccessToken();

    const DEFAULT_METHOD = 'GET';

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const sendRequest = useCallback(async (
        requestConfig: RequestConfig,
        parseData?: (data: T) => void,
        errorCallback?: (error: string) => void
    ) => {
        setIsLoading(true);
        setError(null);

        const buildHeaders = (accessToken?: string): Record<string, string> => {
            const token = accessToken ?? latestAccessToken.current ?? '';

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['x-access-token'] = token;
                headers['Authorization'] = `Bearer ${token}`;
            }

            if (requestConfig.headers) {
                Object.assign(headers, requestConfig.headers);
            }

            return headers;
        };

        const callFetch = async (accessToken?: string) => {
            return await fetch(requestConfig.url, {
                method: requestConfig.method ? requestConfig.method : DEFAULT_METHOD,
                headers: buildHeaders(accessToken),
                body: requestConfig.body ? JSON.stringify(requestConfig.body) : null,
            });
        };

        const parseFetch = async (response: Response) => {
            try {
                const text = await response.text();
                if (!parseData) return;
                
                try {
                    parseData(JSON.parse(text));
                } catch {
                    parseData(text.replace(/^"|"$/g, '') as unknown as T);
                }
            } catch {
                if (parseData) parseData(undefined as unknown as T);
            }
        };

        try {
            let response = await callFetch();

            if (!response.ok) {
                if (response.status === 401) {
                    const loginInfo = await getAccessToken();
                    if (loginInfo instanceof Error) throw loginInfo;

                    response = await callFetch(loginInfo.accessToken);
                    if (!response.ok) throw new Error('Request failed after refresh!');

                    await parseFetch(response);
                } else {
                    const error = await response.json();
                    throw error;
                }
            } else {
                await parseFetch(response);
            }
        } catch (err: unknown) {
            if (errorCallback) errorCallback((err as Error).message || 'Something went wrong!');
            setError((err as Error).message || 'Something went wrong!');
        }

        setIsLoading(false);
    }, [getAccessToken, latestAccessToken]);

    return {
        isLoading,
        error,
        sendRequest,
    };
};

export default useHttp;